// Purpose: /d/:token — external uploader landing page. NO auth, no session.
// Drag-drop zone, per-session file list (Q3: per browser session only —
// refresh wipes; the server has the canonical list). Quota display + remaining
// counter. Continue-adding-files UX.

import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  completeDropboxFile,
  getDropboxMeta,
  presignDropboxFile,
  reportDropboxFileError,
  type DropboxLandingMeta,
} from '../api/public-dropbox.ts';
import { uploadFile, type UploadProgress } from '../lib/upload.ts';
import { MonitoredNotice } from '../components/legal/MonitoredNotice.tsx';
import { Button, Card, ProgressFill } from '../components/ui/index.ts';
import { UploadIcon, ClockIcon } from '../components/icons/index.tsx';

type FileState = {
  file: File;
  progress: number; // 0..1
  stage: UploadProgress['stage'] | 'queued' | 'error';
  error?: string;
};

function fmt(bytes: string | number): string {
  const n = typeof bytes === 'number' ? bytes : Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function DropboxUpload() {
  const { token } = useParams<{ token: string }>();
  const [meta, setMeta] = useState<DropboxLandingMeta | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [files, setFiles] = useState<FileState[]>([]);
  const [linkGone, setLinkGone] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function reloadMeta() {
    if (!token) return;
    try {
      const fresh = await getDropboxMeta(token);
      setMeta(fresh);
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 410) {
        setLinkGone(true);
      } else if (status === 404) {
        setError('This upload link is not valid.');
      } else {
        setError('Unable to load dropbox.');
      }
    }
  }

  useEffect(() => {
    void reloadMeta();
  }, [token]);

  async function uploadOne(file: File, index: number): Promise<void> {
    if (!token) return;
    try {
      await uploadFile(
        file,
        index,
        {
          presign: (input) => presignDropboxFile(token, input),
          complete: ({ fileId, rawDekBase64 }) => completeDropboxFile(token, fileId, rawDekBase64),
          // Commit 8b.13: external-uploader PUT failure reporter.
          reportError: ({ fileId, errorReason }) =>
            reportDropboxFileError(token, fileId, errorReason),
        },
        (p) => {
          setFiles((prev) =>
            prev.map((fs, idx) =>
              idx === index
                ? {
                    ...fs,
                    progress: p.bytesEncrypted / Math.max(1, p.totalBytes),
                    stage: p.stage,
                  }
                : fs
            )
          );
        }
      );
    } catch (err) {
      const status = (err as { response?: { status?: number; data?: { error?: string } } }).response
        ?.status;
      const reason =
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ??
        (err as Error).message;
      if (status === 410) {
        setLinkGone(true);
        throw err;
      }
      setFiles((prev) =>
        prev.map((fs, idx) =>
          idx === index ? { ...fs, stage: 'error', error: String(reason) } : fs
        )
      );
      throw err;
    }
  }

  async function addFiles(incoming: FileList | null) {
    if (!incoming || incoming.length === 0 || !token || !meta) return;
    setError(null);
    const next: FileState[] = [];
    for (const f of Array.from(incoming)) {
      next.push({ file: f, progress: 0, stage: 'queued' });
    }
    const baseIndex = files.length;
    setFiles((prev) => [...prev, ...next]);

    for (let i = 0; i < next.length; i++) {
      const index = baseIndex + i;
      // eslint-disable-next-line security/detect-object-injection -- bounded for-loop index
      const state = next[i]!;
      try {
        await uploadOne(state.file, index);
      } catch (err) {
        // 410 (link gone) is the only condition that aborts the batch;
        // uploadOne already set linkGone state in that case.
        const status = (err as { response?: { status?: number } }).response?.status;
        if (status === 410) break;
        // For all other errors, the row has its own error state — continue.
      }
    }
    // Refresh remaining quota after each batch.
    void reloadMeta();
  }

  async function retryFile(index: number) {
    if (!token) return;
    const target = files[index];
    if (!target) return;
    setError(null);
    setFiles((prev) =>
      prev.map((fs, idx) => {
        if (idx !== index) return fs;
        // Reset stage + progress; drop the error field via destructure so
        // exactOptionalPropertyTypes stays happy (no `error: undefined`).
        const { error: _err, ...rest } = fs;
        void _err;
        return { ...rest, stage: 'queued', progress: 0 };
      })
    );
    try {
      await uploadOne(target.file, index);
    } catch {
      // uploadOne already recorded the per-file error state.
    }
    void reloadMeta();
  }

  if (linkGone) {
    return (
      <div className="flex flex-col gap-8">
        <header className="flex flex-col gap-3">
          <span className="text-label uppercase text-accent-primary">Secure Dropbox</span>
          <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
            Upload link unavailable
          </h1>
          <p className="text-body-l text-fg-secondary">
            This upload link has expired or been revoked. Contact the Apogee team member who shared
            the link with you for a new one.
          </p>
        </header>
      </div>
    );
  }

  if (error && !meta) {
    return (
      <div className="flex flex-col gap-8">
        <header className="flex flex-col gap-3">
          <span className="text-label uppercase text-accent-primary">Secure Dropbox</span>
          <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
            Dropbox unavailable
          </h1>
          <p className="text-body-l text-fg-secondary">{error}</p>
        </header>
      </div>
    );
  }

  if (!meta) return <p className="text-body-m text-fg-secondary">Loading...</p>;

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <span className="text-label uppercase text-accent-primary">Secure Dropbox</span>
        <h1 className="font-display text-display-l text-fg-primary [font-feature-settings:var(--font-feature-display)]">
          Upload to Apogee
        </h1>
        <p className="text-body-l text-fg-secondary">
          <span className="text-fg-primary font-medium">{meta.label}</span>
        </p>
      </header>

      <Card padding="comfortable">
        <div className="flex flex-col gap-1 text-body-s text-fg-secondary">
          <div>
            Capacity: {meta.usedFiles}/{meta.maxFiles} files · {fmt(meta.usedSizeBytes)} of{' '}
            {fmt(meta.maxSizeBytes)} used
          </div>
          {meta.expiresAt ? (
            <div className="inline-flex items-center gap-2 text-body-s text-fg-muted">
              <ClockIcon size={14} aria-hidden />
              <span>Link expires {new Date(meta.expiresAt).toLocaleString()}</span>
            </div>
          ) : null}
        </div>
      </Card>

      <div
        onDragOver={(e) => {
          e.preventDefault();
        }}
        onDrop={(e) => {
          e.preventDefault();
          void addFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className="border-2 border-dashed border-border-default rounded-lg p-10 text-center cursor-pointer hover:border-accent-primary hover:bg-bg-tertiary/40 transition-colors duration-fast ease-out"
        data-testid="dropzone"
      >
        <div className="flex flex-col items-center gap-3">
          <UploadIcon size={32} className="text-accent-primary" aria-hidden />
          <p className="text-body-m text-fg-primary">Drop files here or click to choose.</p>
          <p className="text-body-s text-fg-muted">
            Files are encrypted in your browser before they leave your device.
          </p>
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            void addFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>

      <MonitoredNotice className="mt-4" />

      {error ? (
        <div
          role="alert"
          className="bg-status-danger-muted text-status-danger border border-status-danger/30 rounded-md px-4 py-3 text-body-s"
        >
          {error}
        </div>
      ) : null}

      {files.length === 0 ? (
        <p className="text-body-s text-fg-muted">
          If you uploaded files and refreshed this page, they are safely received. The Apogee team
          can see them.
        </p>
      ) : (
        <Card padding="none">
          <div className="divide-y divide-border-default">
            {files.map((f, i) => (
              <div key={i} className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-body-m text-fg-primary truncate max-w-md">{f.file.name}</div>
                  <div className="text-body-s text-fg-muted">{fmt(f.file.size)}</div>
                </div>
                <div className="h-2 bg-bg-tertiary rounded overflow-hidden">
                  <ProgressFill
                    value={f.progress * 100}
                    className={`h-full ${f.stage === 'error' ? 'bg-status-danger' : 'bg-accent-primary'}`}
                  />
                </div>
                <div
                  className={`text-body-s mt-2 flex items-center justify-between gap-3 ${
                    f.stage === 'error' ? 'text-status-danger' : 'text-fg-muted'
                  }`}
                >
                  <span>
                    {f.stage === 'done'
                      ? 'uploaded'
                      : f.stage === 'error'
                        ? `error: ${f.error ?? 'unknown'}`
                        : f.stage}
                  </span>
                  {f.stage === 'error' ? (
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => void retryFile(i)}
                      data-testid={`retry-upload-${i}`}
                    >
                      Retry
                    </Button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
